import { clearSession, getAccessToken } from '../auth/session';
import type {
  CheckoutRequest,
  CheckoutResponse,
  ErrorResponse,
  FakeCompletionResponse,
  MeResponse,
  OfferingResponse,
  PaymentProviderResponse,
  ProbeResponse,
  StorefrontResponse,
  VersionResponse,
} from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/**
 * fetch with the platform bearer token attached when signed in (ADR-017); a plain
 * pass-through when signed out (the storefront browses anonymously). A 401 means the
 * token expired or was revoked: the session is cleared so the shell falls back to the
 * signed-out header, and the ApiError still surfaces to the caller.
 */
async function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  const token = getAccessToken();
  let res: Response;
  if (token === null) {
    res = init === undefined ? await fetch(input) : await fetch(input, init);
  } else {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);
    res = await fetch(input, { ...init, headers });
  }
  if (res.status === 401) clearSession();
  return res;
}

/** Thrown for any non-2xx response; carries the backend's domain error contract. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: ErrorResponse,
  ) {
    super(`${body.code}: ${body.description}`);
  }
}

/** Throws an {@link ApiError} (with the parsed domain error) for any non-2xx response. */
async function ensureOk(res: Response): Promise<Response> {
  if (!res.ok) throw new ApiError(res.status, (await res.json()) as ErrorResponse);
  return res;
}

const tenantPath = (tenant: string): string => `/tenants/${encodeURIComponent(tenant)}`;

/** GET /api/Version — the deployed artifact identity. */
export async function getVersion(): Promise<VersionResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/Version`));
  return (await res.json()) as VersionResponse;
}

/** GET /api/storefront — the seeded per-provider test tenants + payment-host reachability. */
export async function getStorefront(): Promise<StorefrontResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/storefront`));
  return (await res.json()) as StorefrontResponse;
}

/** GET /api/tenants/{tenant}/offerings — the tenant's catalog (shared fixture: monthly/weekly/day-pass). */
export async function listOfferings(tenant: string): Promise<OfferingResponse[]> {
  const res = await ensureOk(await apiFetch(`${BASE}${tenantPath(tenant)}/offerings`));
  return (await res.json()) as OfferingResponse[];
}

/** GET /api/tenants/{tenant}/payment-provider — the live provider selection relayed from the payment host. */
export async function getPaymentProvider(tenant: string): Promise<PaymentProviderResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}${tenantPath(tenant)}/payment-provider`));
  return (await res.json()) as PaymentProviderResponse;
}

/** POST /api/tenants/{tenant}/checkout — start the real checkout path through the payment host. */
export async function createCheckout(
  tenant: string,
  body: CheckoutRequest,
): Promise<CheckoutResponse> {
  const res = await ensureOk(
    await apiFetch(`${BASE}${tenantPath(tenant)}/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
  return (await res.json()) as CheckoutResponse;
}

/** POST /api/fake-payments/complete — play the fake provider's signed webhook for a session. */
export async function completeFakePayment(sessionId: string): Promise<FakeCompletionResponse> {
  const res = await ensureOk(
    await apiFetch(`${BASE}/fake-payments/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    }),
  );
  return (await res.json()) as FakeCompletionResponse;
}

/** GET /api/me — the caller identity as the server-side middleware validated it (ADR-017 §7). */
export async function getMe(): Promise<MeResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/me`));
  return (await res.json()) as MeResponse;
}

/** GET /api/probes/anonymous — always 200; the probe matrix's baseline row. */
export async function probeAnonymous(): Promise<ProbeResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/probes/anonymous`));
  return (await res.json()) as ProbeResponse;
}

/** GET /api/probes/authenticated — any validated platform token (401 without one). */
export async function probeAuthenticated(): Promise<ProbeResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/probes/authenticated`));
  return (await res.json()) as ProbeResponse;
}

/** GET /api/probes/global-admin — a grant on the internal/admin tenant (403 otherwise). */
export async function probeGlobalAdmin(): Promise<ProbeResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/probes/global-admin`));
  return (await res.json()) as ProbeResponse;
}

/** GET /api/probes/tenants/{tenant}/member — any grant on the tenant (403 otherwise). */
export async function probeTenantMember(tenant: string): Promise<ProbeResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/probes${tenantPath(tenant)}/member`));
  return (await res.json()) as ProbeResponse;
}

/** GET /api/probes/tenants/{tenant}/owner — an Owner grant on the tenant (403 for Admin). */
export async function probeTenantOwner(tenant: string): Promise<ProbeResponse> {
  const res = await ensureOk(await apiFetch(`${BASE}/probes${tenantPath(tenant)}/owner`));
  return (await res.json()) as ProbeResponse;
}
